/**
 * GlobalThemeEditor Component
 * 
 * Editor for global menu theme settings (background, colors, fonts).
 * Displayed when no specific block is selected.
 */

import React, { memo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMenuBuilderStore } from '@/stores/menuBuilderStore';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Separator } from '@/components/ui/separator';
import { Palette, Type, Image as ImageIcon, Sparkles, Upload, Grid, PaintBucket, ShoppingCart } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { supabase } from '@/integrations/supabase/client';
import { BACKGROUND_PATTERNS } from '@/utils/backgroundPatterns';
import { AVAILABLE_FONTS } from '@/utils/fonts';
import { toast } from 'sonner';

interface GlobalThemeEditorProps {
  className?: string;
}

export const GlobalThemeEditor = memo(function GlobalThemeEditor({
  className,
}: GlobalThemeEditorProps) {
  const { t } = useTranslation();
  const globalTheme = useMenuBuilderStore((s) => s.globalThemeOverrides) || {};
  const updateGlobalTheme = useMenuBuilderStore((s) => s.updateGlobalTheme);
  const [isUploading, setIsUploading] = useState(false);

  const handleUpdate = (key: string, value: string) => {
    updateGlobalTheme({ [key]: value });
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setIsUploading(true);
    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `${Math.random().toString(36).substring(2)}.${fileExt}`;
      const filePath = `${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('menu-uploads')
        .upload(filePath, file);

      if (uploadError) throw uploadError;

      const { data } = supabase.storage
        .from('menu-uploads')
        .getPublicUrl(filePath);

      handleUpdate('backgroundImage', data.publicUrl);
      handleUpdate('backgroundType', 'image');
      toast.success(t('menuBuilder.uploadSuccess', 'Image uploaded successfully'));
    } catch (error) {
      console.error('Error uploading image:', error);
      toast.error(t('menuBuilder.uploadError', 'Failed to upload image'));
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className={className}>
      <div className="p-4 border-b">
        <h3 className="font-semibold text-sm flex items-center gap-2">
          <Palette className="h-4 w-4" />
          {t('menuBuilder.globalTheme', 'Global Theme')}
        </h3>
        <p className="text-xs text-muted-foreground mt-1">
          {t('menuBuilder.globalThemeDesc', 'Customize the overall look of your menu')}
        </p>
      </div>

      <div className="p-4 space-y-6">
        {/* Background Section */}
        <div className="space-y-4">
          <h4 className="text-sm font-medium flex items-center gap-2">
            <ImageIcon className="h-3 w-3" />
            {t('menuBuilder.background', 'Background')}
          </h4>

          <Tabs 
            defaultValue={globalTheme.backgroundType || 'solid'} 
            onValueChange={(val) => handleUpdate('backgroundType', val)}
            className="w-full"
          >
            <TabsList className="grid w-full grid-cols-4">
              <TabsTrigger value="solid" title="Solid Color"><PaintBucket className="h-4 w-4" /></TabsTrigger>
              <TabsTrigger value="gradient" title="Gradient"><div className="h-4 w-4 rounded-sm bg-gradient-to-br from-primary to-muted" /></TabsTrigger>
              <TabsTrigger value="image" title="Image"><ImageIcon className="h-4 w-4" /></TabsTrigger>
              <TabsTrigger value="pattern" title="Pattern"><Grid className="h-4 w-4" /></TabsTrigger>
            </TabsList>

            {/* Solid Color */}
            <TabsContent value="solid" className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label>{t('menuBuilder.backgroundColor', 'Color')}</Label>
                <div className="flex gap-2">
                  <Input
                    type="color"
                    value={globalTheme.backgroundColor || '#ffffff'}
                    onChange={(e) => handleUpdate('backgroundColor', e.target.value)}
                    className="h-10 w-12 p-1 cursor-pointer"
                  />
                  <Input
                    type="text"
                    value={globalTheme.backgroundColor || '#ffffff'}
                    onChange={(e) => handleUpdate('backgroundColor', e.target.value)}
                    className="flex-1"
                    placeholder="#ffffff"
                  />
                </div>
              </div>
            </TabsContent>

            {/* Gradient */}
            <TabsContent value="gradient" className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>{t('menuBuilder.gradientStart', 'Start Color')}</Label>
                  <Input
                    type="color"
                    value={globalTheme.gradientStart || '#ffffff'}
                    onChange={(e) => handleUpdate('gradientStart', e.target.value)}
                    className="h-10 w-full p-1 cursor-pointer"
                  />
                </div>
                <div className="space-y-2">
                  <Label>{t('menuBuilder.gradientEnd', 'End Color')}</Label>
                  <Input
                    type="color"
                    value={globalTheme.gradientEnd || '#f3f4f6'}
                    onChange={(e) => handleUpdate('gradientEnd', e.target.value)}
                    className="h-10 w-full p-1 cursor-pointer"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>{t('menuBuilder.gradientDirection', 'Direction')}</Label>
                <Select
                  value={globalTheme.gradientDirection || 'to bottom'}
                  onValueChange={(value) => handleUpdate('gradientDirection', value)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="to bottom">Top to Bottom</SelectItem>
                    <SelectItem value="to right">Left to Right</SelectItem>
                    <SelectItem value="to bottom right">Diagonal</SelectItem>
                    <SelectItem value="45deg">45 Degrees</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </TabsContent>

            {/* Image */}
            <TabsContent value="image" className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label>{t('menuBuilder.uploadImage', 'Upload Image')}</Label>
                <div className="flex items-center gap-2">
                  <Button 
                    variant="outline" 
                    className="w-full" 
                    disabled={isUploading}
                    onClick={() => document.getElementById('bg-image-upload')?.click()}
                  >
                    {isUploading ? (
                      <span className="animate-pulse">Uploading...</span>
                    ) : (
                      <>
                        <Upload className="h-4 w-4 mr-2" />
                        {t('menuBuilder.chooseFile', 'Choose File')}
                      </>
                    )}
                  </Button>
                  <Input
                    id="bg-image-upload"
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={handleFileUpload}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>{t('menuBuilder.imageUrl', 'Or Image URL')}</Label>
                <Input
                  type="text"
                  value={globalTheme.backgroundImage || ''}
                  onChange={(e) => handleUpdate('backgroundImage', e.target.value)}
                  placeholder="https://..."
                />
              </div>

              {globalTheme.backgroundImage && (
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>{t('menuBuilder.backgroundSize', 'Size')}</Label>
                    <Select
                      value={globalTheme.backgroundSize || 'cover'}
                      onValueChange={(value) => handleUpdate('backgroundSize', value)}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="cover">{t('menuBuilder.cover', 'Cover')}</SelectItem>
                        <SelectItem value="contain">{t('menuBuilder.contain', 'Contain')}</SelectItem>
                        <SelectItem value="auto">{t('menuBuilder.auto', 'Auto')}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label>{t('menuBuilder.backgroundPosition', 'Position')}</Label>
                    <Select
                      value={globalTheme.backgroundPosition || 'center'}
                      onValueChange={(value) => handleUpdate('backgroundPosition', value)}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="top">{t('common.top', 'Top')}</SelectItem>
                        <SelectItem value="center">{t('common.center', 'Center')}</SelectItem>
                        <SelectItem value="bottom">{t('common.bottom', 'Bottom')}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              )}
            </TabsContent>

            {/* Pattern */}
            <TabsContent value="pattern" className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-2">
                {Object.values(BACKGROUND_PATTERNS).map((pattern) => (
                  <div
                    key={pattern.id}
                    className={`
                      cursor-pointer rounded-md border-2 p-2 hover:border-primary transition-all
                      ${globalTheme.backgroundPattern === pattern.id ? 'border-primary bg-primary/5' : 'border-transparent bg-muted'}
                    `}
                    onClick={() => handleUpdate('backgroundPattern', pattern.id)}
                  >
                    <div 
                      className="h-16 w-full rounded border bg-white"
                      style={pattern.style}
                    />
                    <p className="text-xs text-center mt-2 font-medium truncate">
                      {pattern.name}
                    </p>
                  </div>
                ))}
              </div>
            </TabsContent>
          </Tabs>
        </div>

        <Separator />

        {/* Animations Section */}
        <div className="space-y-4">
          <h4 className="text-sm font-medium flex items-center gap-2">
            <Sparkles className="h-3 w-3" />
            {t('menuBuilder.animations', 'Animations')}
          </h4>

          <div className="space-y-2">
            <Label>{t('menuBuilder.blockAnimation', 'Block Entry Animation')}</Label>
            <Select
              value={globalTheme.blockAnimation || 'none'}
              onValueChange={(value) => handleUpdate('blockAnimation', value)}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">{t('common.none', 'None')}</SelectItem>
                <SelectItem value="fade-in">{t('menuBuilder.fadeIn', 'Fade In')}</SelectItem>
                <SelectItem value="slide-up">{t('menuBuilder.slideUp', 'Slide Up')}</SelectItem>
                <SelectItem value="scale-up">{t('menuBuilder.scaleUp', 'Scale Up')}</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <Separator />

        {/* Typography Section */}
        <div className="space-y-4">
          <h4 className="text-sm font-medium flex items-center gap-2">
            <Type className="h-3 w-3" />
            {t('menuBuilder.typography', 'Typography')}
          </h4>

          <div className="space-y-2">
            <Label>{t('menuBuilder.fontFamily', 'Font Family')}</Label>
            <Select
              value={globalTheme.fontFamily || 'inherit'}
              onValueChange={(val) => handleUpdate('fontFamily', val)}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder={t('menuBuilder.selectFont', 'Select a font')} />
              </SelectTrigger>
              <SelectContent>
                <div className="max-h-[300px]">
                  {/* System Fonts */}
                  <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground">
                    {t('menuBuilder.systemFonts', 'System Fonts')}
                  </div>
                  {AVAILABLE_FONTS.filter(f => f.type === 'system').map((font) => (
                    <SelectItem 
                      key={font.value} 
                      value={font.value}
                      style={{ fontFamily: font.value }}
                    >
                      {font.label}
                    </SelectItem>
                  ))}

                  <Separator className="my-2" />

                  {/* Sans Serif */}
                  <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground">
                    Sans Serif
                  </div>
                  {AVAILABLE_FONTS.filter(f => f.type === 'google' && f.category === 'sans-serif').map((font) => (
                    <SelectItem 
                      key={font.value} 
                      value={font.value}
                      style={{ fontFamily: font.value }}
                    >
                      {font.label}
                    </SelectItem>
                  ))}

                  <Separator className="my-2" />

                  {/* Serif */}
                  <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground">
                    Serif
                  </div>
                  {AVAILABLE_FONTS.filter(f => f.type === 'google' && f.category === 'serif').map((font) => (
                    <SelectItem 
                      key={font.value} 
                      value={font.value}
                      style={{ fontFamily: font.value }}
                    >
                      {font.label}
                    </SelectItem>
                  ))}

                  <Separator className="my-2" />

                  {/* Display & Handwriting */}
                  <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground">
                    Display & Handwriting
                  </div>
                  {AVAILABLE_FONTS.filter(f => ['display', 'handwriting'].includes(f.category)).map((font) => (
                    <SelectItem 
                      key={font.value} 
                      value={font.value}
                      style={{ fontFamily: font.value }}
                    >
                      {font.label}
                    </SelectItem>
                  ))}
                </div>
              </SelectContent>
            </Select>
          </div>

          {/* Font Weight */}
          <div className="space-y-2">
            <Label>{t('menuBuilder.fontWeight', 'Font Weight')}</Label>
            <Select
              value={globalTheme.fontWeight || 'normal'}
              onValueChange={(val) => handleUpdate('fontWeight', val)}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder={t('menuBuilder.normal', 'Normal')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="normal">{t('menuBuilder.normal', 'Normal')}</SelectItem>
                <SelectItem value="bold">{t('menuBuilder.bold', 'Bold')}</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <Separator />

        {/* Colors Section */}
        <div className="space-y-4">
          <h4 className="text-sm font-medium flex items-center gap-2">
            <Palette className="h-3 w-3" />
            {t('menuBuilder.colors', 'Colors')}
          </h4>
          
          {/* Text Color */}
          <div className="space-y-2">
            <Label>{t('menuBuilder.textColor', 'Text Color')}</Label>
            <div className="flex gap-2">
              <Input
                type="color"
                value={globalTheme.textColor || '#000000'}
                onChange={(e) => handleUpdate('textColor', e.target.value)}
                className="h-10 w-12 p-1 cursor-pointer"
              />
              <Input
                type="text"
                value={globalTheme.textColor || '#000000'}
                onChange={(e) => handleUpdate('textColor', e.target.value)}
                className="flex-1"
                placeholder="#000000"
              />
            </div>
          </div>

          {/* Primary Color */}
          <div className="space-y-2">
            <Label>{t('menuBuilder.primaryColor', 'Primary Color')}</Label>
            <div className="flex gap-2">
              <Input
                type="color"
                value={globalTheme.primaryColor || '#000000'}
                onChange={(e) => handleUpdate('primaryColor', e.target.value)}
                className="h-10 w-12 p-1 cursor-pointer"
              />
              <Input
                type="text"
                value={globalTheme.primaryColor || '#000000'}
                onChange={(e) => handleUpdate('primaryColor', e.target.value)}
                className="flex-1"
                placeholder="#000000"
              />
            </div>
          </div>

          {/* Card Background */}
          <div className="space-y-2">
            <Label>{t('menuBuilder.cardBackground', 'Card Background')}</Label>
            <div className="flex gap-2">
              <Input
                type="color"
                value={globalTheme.cardBackground || '#ffffff'}
                onChange={(e) => handleUpdate('cardBackground', e.target.value)}
                className="h-10 w-12 p-1 cursor-pointer"
              />
              <Input
                type="text"
                value={globalTheme.cardBackground || '#ffffff'}
                onChange={(e) => handleUpdate('cardBackground', e.target.value)}
                className="flex-1"
                placeholder="#ffffff"
              />
            </div>
          </div>
        </div>

        <Separator />

        {/* Cart Section */}
        <div className="space-y-4">
          <h4 className="text-sm font-medium flex items-center gap-2">
            <ShoppingCart className="h-3 w-3" />
            {t('menuBuilder.cart', 'Cart Settings')}
          </h4>

          {/* Position */}
          <div className="space-y-2">
            <Label>{t('menuBuilder.cartPosition', 'Position')}</Label>
            <Select
              value={globalTheme.cartPosition || 'bottom-right'}
              onValueChange={(val) => handleUpdate('cartPosition', val)}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="bottom-right">{t('common.bottomRight', 'Bottom Right')}</SelectItem>
                <SelectItem value="bottom-left">{t('common.bottomLeft', 'Bottom Left')}</SelectItem>
                <SelectItem value="bottom-center">{t('common.bottomCenter', 'Bottom Center')}</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Button Style */}
          <div className="space-y-2">
            <Label>{t('menuBuilder.cartStyle', 'Button Style')}</Label>
            <Select
              value={globalTheme.cartButtonStyle || 'circle'}
              onValueChange={(val) => handleUpdate('cartButtonStyle', val)}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="circle">{t('common.circle', 'Circle')}</SelectItem>
                <SelectItem value="pill">{t('common.pill', 'Pill')}</SelectItem>
                <SelectItem value="square">{t('common.square', 'Square')}</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Button Color */}
          <div className="space-y-2">
            <Label>{t('menuBuilder.cartColor', 'Button Color')}</Label>
            <div className="flex gap-2">
              <Input
                type="color"
                value={globalTheme.cartButtonColor || globalTheme.primaryColor || '#000000'}
                onChange={(e) => handleUpdate('cartButtonColor', e.target.value)}
                className="h-10 w-12 p-1 cursor-pointer"
              />
              <Input
                type="text"
                value={globalTheme.cartButtonColor || globalTheme.primaryColor || '#000000'}
                onChange={(e) => handleUpdate('cartButtonColor', e.target.value)}
                className="flex-1"
                placeholder="#000000"
              />
            </div>
          </div>

          {/* Icon Color */}
          <div className="space-y-2">
            <Label>{t('menuBuilder.cartIconColor', 'Icon Color')}</Label>
            <div className="flex gap-2">
              <Input
                type="color"
                value={globalTheme.cartIconColor || '#ffffff'}
                onChange={(e) => handleUpdate('cartIconColor', e.target.value)}
                className="h-10 w-12 p-1 cursor-pointer"
              />
              <Input
                type="text"
                value={globalTheme.cartIconColor || '#ffffff'}
                onChange={(e) => handleUpdate('cartIconColor', e.target.value)}
                className="flex-1"
                placeholder="#ffffff"
              />
            </div>
          </div>
        </div>

        <Separator />

        {/* Typography Section (Placeholder for now) */}
        <div className="space-y-4">
          <h4 className="text-sm font-medium flex items-center gap-2">
            <Type className="h-3 w-3" />
            {t('menuBuilder.typography', 'Typography')}
          </h4>
          
          <div className="space-y-2">
            <Label>{t('menuBuilder.fontFamily', 'Font Family')}</Label>
            <Input
              type="text"
              value={globalTheme.fontFamily || 'Inter'}
              onChange={(e) => handleUpdate('fontFamily', e.target.value)}
              placeholder="Inter, sans-serif"
            />
            <p className="text-xs text-muted-foreground">
              {t('menuBuilder.fontHelp', 'Enter a valid CSS font-family stack')}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
});
